<template>
  <v-dialog v-model="dialog" max-width="560" persistent>
    <v-card :loading="isSaving || isLoadingLots">
      <v-card-title class="headline">
        {{ $t("STOCK.INVENTORY.BLOCK_STOCK") }}
      </v-card-title>
      <v-card-subtitle v-if="productLabel" class="pb-0">
        {{ productLabel }}
        <span v-if="warehouseLabel" class="grey--text"> · {{ warehouseLabel }}</span>
      </v-card-subtitle>
      <v-card-text>
        <v-alert v-if="errorMessage" type="error" dense text class="mb-3">{{ errorMessage }}</v-alert>
        <v-alert
          v-else-if="!isLoadingLots && lotOptions.length === 0"
          type="warning"
          dense
          text
          class="mb-3"
        >
          {{ $t("STOCK.INVENTORY.BLOCK_NO_LOTS") }}
        </v-alert>
        <v-select
          v-model="form.stock_lot_id"
          :items="lotOptions"
          item-value="stock_lot_id"
          item-text="label"
          :label="$t('STOCK.INVENTORY.LOT')"
          :disabled="!!lockedLotId || isLoadingLots || lotOptions.length === 0"
          :loading="isLoadingLots"
          outlined
          dense
          hide-details="auto"
          class="mb-3"
          @change="onLotChange"
        />
        <v-text-field
          v-model.number="form.quantity"
          type="number"
          step="0.0001"
          min="0.0001"
          :label="$t('STOCK.INVENTORY.BLOCK_QUANTITY')"
          :hint="maxHint"
          persistent-hint
          outlined
          dense
          class="mb-3"
        />
        <v-textarea
          v-model="form.reason"
          :label="$t('STOCK.INVENTORY.BLOCK_REASON') + '*'"
          outlined
          dense
          rows="3"
          auto-grow
          hide-details="auto"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="isSaving" @click="dialog = false">
          {{ $t("GENERAL.LABELS.CANCEL") }}
        </v-btn>
        <v-btn color="warning" dark :loading="isSaving" :disabled="!canSubmit" @click="submit">
          {{ $t("STOCK.INVENTORY.BLOCK_STOCK") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { CREATE_STOCK_QUALITY_HOLD } from "@/store/stock/stockQualityHolds.module";
import { GET_STOCK_SALE_SUGGEST_DRAFT_LOTS } from "@/store/stock/stockSales.module";
import { formatStockQuantity } from "@/common/stock/stockNumberFormat";

export default {
  name: "StockQualityHoldDialog",
  props: {
    value: { type: Boolean, default: false },
    warehouseId: { type: Number, default: null },
    warehouseLabel: { type: String, default: "" },
    stockProductId: { type: Number, default: null },
    productLabel: { type: String, default: "" },
    stockLotId: { type: Number, default: null },
    asOfDate: { type: String, default: null },
  },
  data() {
    return {
      isSaving: false,
      isLoadingLots: false,
      errorMessage: "",
      lotOptions: [],
      form: {
        stock_lot_id: null,
        quantity: null,
        reason: "",
      },
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    lockedLotId() {
      return this.stockLotId ? Number(this.stockLotId) : null;
    },
    selectedLot() {
      const selectedId = Number(this.form.stock_lot_id);
      if (!selectedId) {
        return null;
      }
      return this.lotOptions.find((lot) => Number(lot.stock_lot_id) === selectedId) || null;
    },
    maxBlockable() {
      if (!this.selectedLot) {
        return 0;
      }
      const onHand = Number(this.selectedLot.quantity_on_hand || 0);
      const reserved = Number(this.selectedLot.reserved_quantity || 0);
      const blocked = Number(this.selectedLot.blocked_quantity || 0);
      return Math.max(0, onHand - reserved - blocked);
    },
    maxHint() {
      return this.$t("STOCK.INVENTORY.BLOCK_MAX_HINT", {
        qty: this.formatQty(this.maxBlockable),
      });
    },
    canSubmit() {
      return (
        !!this.warehouseId
        && !!this.stockProductId
        && !!this.form.stock_lot_id
        && Number(this.form.quantity) > 0
        && Number(this.form.quantity) <= this.maxBlockable + 0.0001
        && String(this.form.reason || "").trim().length > 0
      );
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.resetForm();
        this.loadLots();
      }
    },
  },
  methods: {
    formatQty(value) {
      return formatStockQuantity(value);
    },
    resetForm() {
      this.errorMessage = "";
      this.lotOptions = [];
      this.form = {
        stock_lot_id: this.lockedLotId,
        quantity: null,
        reason: "",
      };
    },
    onLotChange() {
      this.form.quantity = this.maxBlockable > 0 ? this.maxBlockable : null;
    },
    loadLots() {
      if (!this.warehouseId || !this.stockProductId) {
        this.lotOptions = [];
        this.errorMessage = this.$t("STOCK.INVENTORY.BLOCK_LOAD_LOTS_ERROR");
        return;
      }
      this.isLoadingLots = true;
      this.errorMessage = "";
      this.$store
        .dispatch(GET_STOCK_SALE_SUGGEST_DRAFT_LOTS, {
          params: {
            source_warehouse_id: this.warehouseId,
            stock_product_ids: [this.stockProductId],
            as_of_date: this.asOfDate,
            availability_mode: "write_off",
          },
        })
        .then((lots) => {
          const list = Array.isArray(lots) ? lots : lots?.lots || lots?.items || [];
          this.lotOptions = list.map((lot) => ({
            ...lot,
            stock_lot_id: Number(lot.stock_lot_id),
            label: `${lot.lot_number || lot.stock_lot_id} (${this.formatQty(lot.quantity_on_hand)} on hand, ${this.formatQty(lot.blocked_quantity || 0)} blocked)`,
          }));
          if (this.lockedLotId) {
            this.form.stock_lot_id = this.lockedLotId;
          } else if (this.lotOptions.length === 1) {
            this.form.stock_lot_id = this.lotOptions[0].stock_lot_id;
          }
          this.onLotChange();
        })
        .catch((err) => {
          this.errorMessage = err?.message || this.$t("STOCK.INVENTORY.BLOCK_LOAD_LOTS_ERROR");
          this.lotOptions = [];
        })
        .finally(() => {
          this.isLoadingLots = false;
        });
    },
    submit() {
      if (!this.canSubmit) {
        return;
      }
      this.isSaving = true;
      this.errorMessage = "";
      this.$store
        .dispatch(CREATE_STOCK_QUALITY_HOLD, {
          warehouse_id: this.warehouseId,
          stock_product_id: this.stockProductId,
          stock_lot_id: Number(this.form.stock_lot_id),
          quantity: Number(this.form.quantity),
          reason: String(this.form.reason).trim(),
          as_of_date: this.asOfDate || undefined,
        })
        .then(() => {
          this.dialog = false;
          this.$emit("saved");
        })
        .catch((err) => {
          const messages = err?.errors
            ? Object.values(err.errors).flat().join(" ")
            : err?.message;
          this.errorMessage = messages || this.$t("STOCK.INVENTORY.BLOCK_SAVE_ERROR");
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
  },
};
</script>
